import type * as React from "react";

/** Matches any Tailwind z-index utility, arbitrary (`z-[1500]`) or scale (`z-50`). */
const Z_INDEX_UTILITY = /(?:^|\s)-?z-(?:\[[^\]]+\]|\(--[^)]+\)|\w+)/;

/**
 * The default stacking layer for a portalled surface, expressed as an inline
 * style — unless the caller has already asked for one in `className`.
 *
 * Two constraints pull in opposite directions here, and this is what satisfies
 * both.
 *
 * **Why inline rather than a `z-[n]` class.** Handsontable builds CSS selectors
 * by interpolating the class names of ancestor elements. A bracketed Tailwind
 * name on a portalled ancestor became `div.z-,,,B1500,,,D ...` and threw
 * `SyntaxError: not a valid selector`, taking the AS/400 grid down with it
 * (#6211). Grids render inside dialogs, so the same hazard applies here.
 *
 * **Why it has to yield to `className`.** An inline style beats any class, so a
 * default written inline would silently defeat every caller override — and
 * those overrides are load-bearing. `SheetContent`'s `overlayClassName` exists
 * precisely so a sheet opened inside a dialog can raise both of its halves
 * above the dialog's layer; with an unconditional inline default it would
 * appear to work and change nothing.
 *
 * So: the class wins when there is one, and otherwise the inline default
 * applies without ever putting a bracketed name on a portalled ancestor.
 */
export function layerStyle(
  zIndex: number,
  className: string | undefined,
): React.CSSProperties | undefined {
  if (className && Z_INDEX_UTILITY.test(className)) {
    return undefined;
  }
  return { zIndex };
}
